import LeaseRecordInput from 'src/components/leases/LeaseRecordInput';
import React, { useState, useEffect } from 'react';
import useFetch from 'src/components/leases/useLeaseRecordsFetch';
import { getLeaseRecordsApiErrorMessage, ILeaseRecordsApiError, LeaseRecordsErrorAlert, parseLeaseRecordsApiError } from '../LeaseRecordsApiError';
import { ILeaseIssue, ILeaseMaster } from 'src/models/dlcLease/ILeaseMaster';
import { IIssuePayload } from 'src/models/dlcLease/IIssuePayload';
import { BiPencil, BiPlus, BiX, BiMessageDetail, BiDownload, BiEnvelope } from 'react-icons/bi';
import { Loader2 } from 'src/components/common/icons';
import * as styles from './styles';

// --- Interfaces ---
interface IIssueStatus {
    code: number;
    dscr: string;
}
interface IIssueCode {
    code: number;
    dscr: string;
    addlInfo?: number;
}
interface IIssueAction {
    liId: number;
    lidId: number;
    tranDate: string;
    tranBy: string;
    tranByNo: number;
    tranNote: string;
}

interface IIssuesSectionProps {
    leaseMaster: ILeaseMaster;
}

// --- Main Component ---
const IssuesSection: React.FC<IIssuesSectionProps> = ({ leaseMaster }) => {
    const [issues, setIssues] = useState<ILeaseIssue[]>(leaseMaster.issues || []);
    const [statuses, setStatuses] = useState<IIssueStatus[]>([]);
    const [issueCodes, setIssueCodes] = useState<IIssueCode[]>([]);

    const [expandedIssue, setExpandedIssue] = useState<number | null>(null);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showExportDialog, setShowExportDialog] = useState(false);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [editedIssue, setEditedIssue] = useState<ILeaseIssue | null>(null);
    const [closingIssueId, setClosingIssueId] = useState<number | null>(null);
    const [resolvingIssueId, setResolvingIssueId] = useState<number | null>(null);
    const [resolutionError, setResolutionError] = useState<{ issueId: number; message: string } | null>(null);
    const [emailingIssueId, setEmailingIssueId] = useState<number | null>(null);
    const [emailFeedback, setEmailFeedback] = useState<{ issueId: number; message: string; isError: boolean } | null>(null);
    const [requestError, setRequestError] = useState<ILeaseRecordsApiError | null>(null);

    const { get, post, put, response } = useFetch();

    useEffect(() => {
        const fetchRefs = async () => {
            const [statusData, codeData] = await Promise.all([get('/v1.0/leases/master/issue-statuses'), get('/v1.0/leases/master/issue-codes')]);
            if (response.ok) {
                setStatuses(statusData || []);
                setIssueCodes(codeData || []);
            }
        };
        fetchRefs();
        // Reference data is loaded once when the section mounts. use-http does not
        // guarantee stable method/response identities between renders.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setIssues(leaseMaster.issues || []);
    }, [leaseMaster.issues]);

    useEffect(() => {
        setExpandedIssue(null);
        setShowCreateDialog(false);
        setShowExportDialog(false);
        setEditingIndex(null);
        setEditedIssue(null);
        setClosingIssueId(null);
        setResolvingIssueId(null);
        setResolutionError(null);
        setEmailingIssueId(null);
        setEmailFeedback(null);
        setRequestError(null);
    }, [leaseMaster.lsNo]);

    const onCreated = (newIssue: ILeaseIssue) => {
        setIssues((prev) => [{ ...newIssue, actionCount: 0 }, ...prev]);
    };

    const updateIssueActionCount = (liId: number | undefined, actionCount: number) => {
        if (!liId) {
            return;
        }
        setIssues((prev) =>
            prev.map((issue) =>
                issue.liId === liId
                    ? {
                          ...issue,
                          actionCount,
                      }
                    : issue
            )
        );
    };

    const handleSaveInline = async (index: number) => {
        if (!editedIssue) {
            return;
        }
        setRequestError(null);
        await put(`/v1.0/leases/master/issues/${editedIssue.liId}`, editedIssue);

        if (response.ok) {
            const updatedList = [...issues];
            updatedList[index] = editedIssue;
            setIssues(updatedList);
            setEditingIndex(null);
            setEditedIssue(null);
        } else {
            setRequestError(parseLeaseRecordsApiError(response.data, response.status));
        }
    };

    const beginEditingIssue = (index: number, issue: ILeaseIssue) => {
        setClosingIssueId(null);
        setResolutionError(null);
        setEditingIndex(index);
        setEditedIssue(issue);
    };

    const resolveIssue = async (issue: ILeaseIssue, status: 1 | 2) => {
        if (!issue.liId) {
            return;
        }

        setResolvingIssueId(issue.liId);
        setResolutionError(null);
        await put(`/v1.0/leases/master/issues/${issue.liId}`, { ...issue, status });

        if (response.ok) {
            setIssues((prev) =>
                prev.map((currentIssue) => (currentIssue.liId === issue.liId ? { ...currentIssue, status, closeDate: new Date() } : currentIssue))
            );
            setClosingIssueId(null);
        } else {
            setResolutionError({ issueId: issue.liId, message: getLeaseRecordsApiErrorMessage(response.data, response.status) });
        }

        setResolvingIssueId(null);
    };

    const handleEmailLandman = async (issue: ILeaseIssue) => {
        if (!issue.liId || !issue.lmNo) {
            return;
        }

        setEmailingIssueId(issue.liId);
        setEmailFeedback(null);
        try {
            const result = await post(`/v1.0/leases/master/issues/${issue.liId}/email-landman`, {});

            setEmailFeedback({
                issueId: issue.liId,
                message: response.ok
                    ? result?.message || 'Email sent to the assigned landman.'
                    : getLeaseRecordsApiErrorMessage(result || response.data, response.status),
                isError: !response.ok,
            });
        } catch {
            setEmailFeedback({
                issueId: issue.liId,
                message: 'The email could not be sent. Please try again.',
                isError: true,
            });
        } finally {
            setEmailingIssueId(null);
        }
    };

    // --- FIXED STATUS LOGIC ---
    const getStatusMeta = (statusCode?: number) => {
        const found = statuses.find((s) => s.code === statusCode);
        const text = found ? found.dscr : 'Unknown';

        // Default to gray
        let color = styles.colors.gray500;

        // 0 = Pending (OPEN) -> Red
        if (statusCode === 0) {
            color = styles.colors.danger;
        } else if (statusCode === 1) {
            // 1 = Resolved Good (CLOSED) -> Green
            color = styles.colors.success;
        } else if (statusCode === 2) {
            // 2 = Resolved Bad (CLOSED) -> Orange/Warning
            color = styles.colors.warning;
        }

        return { text, color };
    };

    const getIssueCodeText = (codeId?: number) => {
        const found = issueCodes.find((c) => c.code === codeId);
        return found ? found.dscr : '—';
    };

    const formatDate = (date?: Date | string): string => {
        if (!date) {
            return '—';
        }
        return new Date(date).toLocaleDateString();
    };

    const totalActions = issues.reduce((sum, issue) => sum + (issue.actionCount || 0), 0);
    const openIssueCount = issues.filter((issue) => issue.status === 0).length;
    const closedIssueCount = issues.length - openIssueCount;

    return (
        <div style={styles.sectionContainer}>
            <LeaseRecordsErrorAlert error={requestError} onDismiss={() => setRequestError(null)} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <div>
                    <h2 style={{ ...styles.sectionHeader, margin: 0 }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={styles.colors.primary} strokeWidth="2">
                                <circle cx="12" cy="12" r="10" />
                                <path d="M12 6v6l4 2" />
                            </svg>
                            Issues
                        </span>
                    </h2>
                    {issues.length > 0 && (
                        <div style={localStyles.summaryChips}>
                            <SummaryChip label="Issues" value={issues.length} tone="primary" />
                            <SummaryChip label="Open" value={openIssueCount} tone="danger" />
                            <SummaryChip label="Closed" value={closedIssueCount} tone="success" />
                            <SummaryChip label="Actions" value={totalActions} tone="neutral" />
                        </div>
                    )}
                    <div style={localStyles.leaseLandman}>
                        Lease Landman: {leaseMaster.landmanName || 'Not assigned'}
                        {leaseMaster.lmNo ? ` (#${leaseMaster.lmNo})` : ''}
                    </div>
                </div>

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button onClick={() => setShowExportDialog(true)} style={styles.buttonSecondary} title="Export to Excel">
                        <BiDownload style={{ marginRight: '0.25rem' }} /> Export
                    </button>
                    <button onClick={() => setShowCreateDialog(true)} style={styles.buttonPrimary}>
                        <BiPlus style={{ marginRight: '0.25rem' }} /> New Issue
                    </button>
                </div>
            </div>

            {issues.length === 0 ? (
                <div style={styles.emptyState}>
                    <p style={{ color: styles.colors.gray500 }}>No issues reported.</p>
                </div>
            ) : (
                <div style={{ display: 'grid', gap: '1rem' }}>
                    {issues.map((issue, index) => {
                        const statusMeta = getStatusMeta(issue.status);
                        const isExpanded = expandedIssue === index;
                        const isEditing = editingIndex === index;

                        return (
                            <div
                                key={index}
                                style={{
                                    ...styles.cardBase,
                                    border: `1px solid ${isExpanded ? styles.colors.primary : styles.colors.border}`,
                                    transition: 'all 0.2s ease',
                                    cursor: isEditing ? 'default' : 'pointer',
                                }}
                                onClick={() => !isEditing && setExpandedIssue(isExpanded ? null : index)}
                            >
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                                    <div style={{ minWidth: '140px' }}>
                                        {' '}
                                        {/* Increased width for longer status text */}
                                        {isEditing ? (
                                            <select
                                                value={editedIssue?.status}
                                                onChange={(e) =>
                                                    setEditedIssue((current) => (current ? { ...current, status: Number(e.target.value) } : current))
                                                }
                                                onClick={(e) => e.stopPropagation()}
                                                style={localStyles.selectInput}
                                            >
                                                {statuses.map((s) => (
                                                    <option key={s.code} value={s.code}>
                                                        {s.dscr}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            <span
                                                style={{
                                                    ...styles.badge,
                                                    backgroundColor: statusMeta.color + '20',
                                                    color: statusMeta.color,
                                                    border: `1px solid ${statusMeta.color}55`,
                                                    display: 'block',
                                                    textAlign: 'center',
                                                    fontSize: '0.9rem',
                                                    fontWeight: 700,
                                                    padding: '0.4rem 0.75rem',
                                                }}
                                            >
                                                {statusMeta.text}
                                            </span>
                                        )}
                                    </div>

                                    <div style={{ flex: 1 }}>
                                        {isEditing ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                                <LeaseRecordInput
                                                    type="text"
                                                    value={editedIssue?.isDscr || ''}
                                                    onChange={(e) =>
                                                        setEditedIssue((current) => (current ? { ...current, isDscr: e.target.value } : current))
                                                    }
                                                    style={localStyles.textInput}
                                                    onClick={(e) => e.stopPropagation()}
                                                    placeholder="Description"
                                                />
                                                <div style={localStyles.grid2}>
                                                    <LeaseRecordInput
                                                        type="text"
                                                        value={editedIssue?.lmName || ''}
                                                        onChange={(e) =>
                                                            setEditedIssue((current) => (current ? { ...current, lmName: e.target.value } : current))
                                                        }
                                                        style={localStyles.textInput}
                                                        onClick={(e) => e.stopPropagation()}
                                                        placeholder="Landman Name"
                                                    />
                                                    <LeaseRecordInput
                                                        type="number"
                                                        value={editedIssue?.lmNo || ''}
                                                        onChange={(e) =>
                                                            setEditedIssue((current) =>
                                                                current ? { ...current, lmNo: Number(e.target.value) } : current
                                                            )
                                                        }
                                                        style={localStyles.textInput}
                                                        onClick={(e) => e.stopPropagation()}
                                                        placeholder="Landman No"
                                                    />
                                                </div>
                                                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSaveInline(index);
                                                        }}
                                                        style={localStyles.saveBtn}
                                                    >
                                                        Save
                                                    </button>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setEditingIndex(null);
                                                        }}
                                                        style={localStyles.cancelBtn}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div>
                                                <div style={{ fontWeight: 600, color: styles.colors.gray900, marginBottom: '0.25rem' }}>
                                                    {issue.isDscr || <em>No description provided</em>}
                                                </div>
                                                <div
                                                    style={{
                                                        fontSize: '0.85rem',
                                                        color: styles.colors.gray500,
                                                        display: 'flex',
                                                        gap: '1rem',
                                                        alignItems: 'center',
                                                        flexWrap: 'wrap',
                                                    }}
                                                >
                                                    <span>
                                                        Opened {formatDate(issue.openDate)} by {issue.openBy || 'System'}
                                                    </span>
                                                    {issue.lmName && <span>&bull; Landman: {issue.lmName}</span>}
                                                    <span style={localStyles.actionCountPill}>
                                                        <BiMessageDetail size={14} /> {issue.actionCount || 0} action
                                                        {(issue.actionCount || 0) === 1 ? '' : 's'}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {!isEditing && (
                                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    void handleEmailLandman(issue);
                                                }}
                                                style={{
                                                    ...localStyles.emailButton,
                                                    opacity: !issue.lmNo || emailingIssueId === issue.liId ? 0.55 : 1,
                                                    cursor: !issue.lmNo || emailingIssueId === issue.liId ? 'not-allowed' : 'pointer',
                                                }}
                                                disabled={!issue.lmNo || emailingIssueId === issue.liId}
                                                title={
                                                    issue.lmNo ? 'Email this issue to the assigned landman' : 'Assign a landman before sending email'
                                                }
                                            >
                                                {emailingIssueId === issue.liId ? (
                                                    <Loader2 style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />
                                                ) : (
                                                    <BiEnvelope size={17} />
                                                )}
                                                {emailingIssueId === issue.liId ? 'Sending...' : 'Email Landman'}
                                            </button>
                                            {issue.status === 0 && closingIssueId !== issue.liId && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setResolutionError(null);
                                                        setClosingIssueId(issue.liId || null);
                                                    }}
                                                    style={localStyles.closeIssueButton}
                                                    title="Choose how this issue was resolved"
                                                >
                                                    Close Issue
                                                </button>
                                            )}
                                            {issue.status === 0 && closingIssueId === issue.liId && (
                                                <div style={localStyles.resolutionChoices} onClick={(e) => e.stopPropagation()}>
                                                    <button
                                                        onClick={() => void resolveIssue(issue, 1)}
                                                        style={localStyles.resolvedGoodButton}
                                                        disabled={resolvingIssueId === issue.liId}
                                                    >
                                                        {resolvingIssueId === issue.liId ? 'Saving...' : 'Good Lease'}
                                                    </button>
                                                    <button
                                                        onClick={() => void resolveIssue(issue, 2)}
                                                        style={localStyles.resolvedBadButton}
                                                        disabled={resolvingIssueId === issue.liId}
                                                    >
                                                        {resolvingIssueId === issue.liId ? 'Saving...' : 'Bad Lease'}
                                                    </button>
                                                    <button
                                                        onClick={() => setClosingIssueId(null)}
                                                        style={localStyles.resolutionCancelButton}
                                                        disabled={resolvingIssueId === issue.liId}
                                                        aria-label="Cancel closing issue"
                                                    >
                                                        <BiX size={18} />
                                                    </button>
                                                </div>
                                            )}
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    beginEditingIssue(index, issue);
                                                }}
                                                style={localStyles.editIssueButton}
                                                title="Edit Issue"
                                                aria-label="Edit Issue"
                                            >
                                                <BiPencil size={22} />
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {issue.liId && resolutionError?.issueId === issue.liId && (
                                    <div
                                        role="alert"
                                        style={{ ...localStyles.emailFeedback, color: styles.colors.danger, backgroundColor: '#FEF2F2' }}
                                    >
                                        {resolutionError.message}
                                    </div>
                                )}

                                {issue.liId && emailFeedback?.issueId === issue.liId && (
                                    <div
                                        role={emailFeedback.isError ? 'alert' : 'status'}
                                        style={{
                                            ...localStyles.emailFeedback,
                                            color: emailFeedback.isError ? styles.colors.danger : styles.colors.success,
                                            backgroundColor: emailFeedback.isError ? '#FEF2F2' : '#F0FDF4',
                                        }}
                                    >
                                        {emailFeedback.message}
                                    </div>
                                )}

                                {isExpanded && !isEditing && (
                                    <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: `1px solid ${styles.colors.border}` }}>
                                        <div style={localStyles.grid2}>
                                            <InfoItem label="Issue Code" value={getIssueCodeText(issue.isCode)} />
                                            <InfoItem label="Landman" value={`${issue.lmName || ''} (${issue.lmNo || '-'})`} />
                                            <InfoItem label="Date Opened" value={formatDate(issue.openDate)} />
                                            <InfoItem label="Date Closed" value={formatDate(issue.closeDate)} />
                                        </div>
                                        <IssueActionsPanel issue={issue} onActionCountChange={(count) => updateIssueActionCount(issue.liId, count)} />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* --- Modals --- */}

            {showCreateDialog && (
                <CreateIssueDialog
                    lsNo={leaseMaster.lsNo}
                    defaultLmNo={leaseMaster.lmNo}
                    defaultLmName={leaseMaster.landmanName}
                    statuses={statuses}
                    issueCodes={issueCodes}
                    onClose={() => setShowCreateDialog(false)}
                    onCreated={onCreated}
                />
            )}

            {showExportDialog && (
                <ExportIssuesDialog clNo={leaseMaster.clNo || 0} prNo={leaseMaster.prNo || 0} onClose={() => setShowExportDialog(false)} />
            )}
        </div>
    );
};

const InfoItem = ({ label, value }: { label: string; value: string }) => (
    <div>
        <div style={{ fontSize: '0.75rem', color: styles.colors.gray500, textTransform: 'uppercase' }}>{label}</div>
        <div style={{ fontSize: '0.95rem', color: styles.colors.gray800, fontWeight: 500 }}>{value}</div>
    </div>
);

const SummaryChip = ({ label, value, tone }: { label: string; value: number; tone: 'primary' | 'danger' | 'success' | 'neutral' }) => {
    const toneColor =
        tone === 'primary'
            ? styles.colors.primary
            : tone === 'danger'
            ? styles.colors.danger
            : tone === 'success'
            ? styles.colors.success
            : styles.colors.gray600;

    return (
        <span
            style={{
                ...localStyles.summaryChip,
                borderColor: `${toneColor}55`,
                color: toneColor,
                backgroundColor: `${toneColor}12`,
            }}
        >
            <strong>{value}</strong> {label}
        </span>
    );
};

// --- 1. Export Dialog (Updated with Correct Filter Descriptions) ---
const ExportIssuesDialog: React.FC<{ clNo: number; prNo: number; onClose: () => void }> = ({ clNo, prNo, onClose }) => {
    const { get, response } = useFetch();
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [loading, setLoading] = useState(false);
    const [requestError, setRequestError] = useState<ILeaseRecordsApiError | null>(null);

    const handleExport = async () => {
        setLoading(true);
        setRequestError(null);
        try {
            await get(`/v1.0/leases/master/issues/report?clNo=${clNo}&prNo=${prNo}&status=${statusFilter}`);

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `LeaseIssues_${clNo}_${prNo}_${statusFilter}.xlsx`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                onClose();
            } else {
                setRequestError(
                    parseLeaseRecordsApiError(response.data || { title: 'Failed to generate report. Please check server logs.' }, response.status)
                );
            }
        } catch (error) {
            setRequestError(parseLeaseRecordsApiError({ title: 'Error connecting to server.' }, 500));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={localStyles.modalOverlay}>
            <div style={localStyles.modalContent}>
                <div style={localStyles.modalHeader}>
                    <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <BiDownload /> Export Issues Report
                    </h3>
                    <button onClick={onClose} style={localStyles.iconButton}>
                        <BiX size={24} />
                    </button>
                </div>
                <LeaseRecordsErrorAlert error={requestError} onDismiss={() => setRequestError(null)} />
                <div style={localStyles.modalBody}>
                    <p style={{ color: styles.colors.gray600, marginBottom: '1rem', fontSize: '0.9rem' }}>
                        Select which issues to include in the Excel download:
                    </p>
                    <label style={localStyles.label}>Filter By Status</label>
                    <select style={localStyles.selectInput} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                        <option value="ALL">All Issues</option>
                        <option value="OPEN">Open (Pending)</option>
                        <option value="CLOSE">Closed (Resolved)</option>
                    </select>
                </div>
                <div style={localStyles.modalFooter}>
                    <button onClick={onClose} style={styles.buttonSecondary} disabled={loading}>
                        Cancel
                    </button>
                    <button onClick={handleExport} style={styles.buttonPrimary} disabled={loading}>
                        {loading ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />
                                Generating...
                            </span>
                        ) : (
                            'Download Excel'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- 2. Create Dialog (Updated Default Status to 0) ---
const CreateIssueDialog: React.FC<{
    lsNo: number;
    defaultLmNo?: number;
    defaultLmName?: string;
    statuses: IIssueStatus[];
    issueCodes: IIssueCode[];
    onClose: () => void;
    onCreated: (newIssue: ILeaseIssue) => void;
}> = ({ lsNo, defaultLmNo, defaultLmName, statuses, issueCodes, onClose, onCreated }) => {
    const { post, response, loading } = useFetch();

    // FIX: Default status to 0 (Pending) instead of 1
    const [form, setForm] = useState<Partial<IIssuePayload>>({
        lsNo,
        status: 0,
        openBy: 'Current User',
        openByNo: 9999,
        lmNo: defaultLmNo,
        lmName: defaultLmName,
    });
    const [requestError, setRequestError] = useState<ILeaseRecordsApiError | null>(null);

    const handleSubmit = async () => {
        if (!form.isDscr) {
            return alert('Description is required.');
        }
        if (form.isCode === undefined || form.isCode === null) {
            return alert('Please select an Issue Type.');
        }
        setRequestError(null);
        await post('/v1.0/leases/master/issue', form);
        if (response.ok) {
            const newIssue = await response.json();
            onCreated(newIssue);
            onClose();
        } else {
            setRequestError(parseLeaseRecordsApiError(response.data, response.status));
        }
    };

    return (
        <div style={localStyles.modalOverlay}>
            <div style={localStyles.modalContent}>
                <div style={localStyles.modalHeader}>
                    <h3 style={{ margin: 0 }}>Create New Issue</h3>
                    <button onClick={onClose} style={localStyles.iconButton}>
                        <BiX size={24} />
                    </button>
                </div>
                <LeaseRecordsErrorAlert error={requestError} onDismiss={() => setRequestError(null)} />
                <div style={localStyles.modalBody}>
                    <div style={{ marginBottom: '1rem' }}>
                        <label style={localStyles.label}>
                            Description <span style={{ color: 'red' }}>*</span>
                        </label>
                        <textarea
                            rows={3}
                            style={localStyles.textarea}
                            value={form.isDscr || ''}
                            onChange={(e) => setForm({ ...form, isDscr: e.target.value })}
                        />
                    </div>
                    <div style={localStyles.grid2}>
                        <div>
                            <label style={localStyles.label}>Status</label>
                            <select
                                style={localStyles.selectInput}
                                value={form.status}
                                onChange={(e) => setForm({ ...form, status: Number(e.target.value) })}
                            >
                                {statuses.map((s) => (
                                    <option key={s.code} value={s.code}>
                                        {s.dscr}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label style={localStyles.label}>
                                Type <span style={{ color: 'red' }}>*</span>
                            </label>
                            <select
                                style={localStyles.selectInput}
                                value={form.isCode ?? ''}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        isCode: e.target.value === '' ? undefined : Number(e.target.value),
                                    })
                                }
                            >
                                <option value="">Select...</option>
                                {issueCodes.map((c) => (
                                    <option key={c.code} value={c.code}>
                                        {c.dscr}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div style={{ ...localStyles.grid2, marginTop: '1rem' }}>
                        <div>
                            <label style={localStyles.label}>Landman No</label>
                            <LeaseRecordInput
                                type="number"
                                style={localStyles.textInput}
                                value={form.lmNo || ''}
                                onChange={(e) => setForm({ ...form, lmNo: Number(e.target.value) })}
                            />
                        </div>
                        <div>
                            <label style={localStyles.label}>Landman Name</label>
                            <LeaseRecordInput
                                type="text"
                                style={localStyles.textInput}
                                value={form.lmName || ''}
                                onChange={(e) => setForm({ ...form, lmName: e.target.value })}
                            />
                        </div>
                    </div>
                </div>
                <div style={localStyles.modalFooter}>
                    <button onClick={onClose} style={styles.buttonSecondary} disabled={loading}>
                        Cancel
                    </button>
                    <button onClick={handleSubmit} style={styles.buttonPrimary} disabled={loading}>
                        {loading ? <Loader2 style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} /> : 'Create'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const IssueActionsPanel: React.FC<{ issue: ILeaseIssue; onActionCountChange: (count: number) => void }> = ({ issue, onActionCountChange }) => {
    const { get, post, response } = useFetch();
    const [actions, setActions] = useState<IIssueAction[]>([]);
    const [newAction, setNewAction] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const fetchActions = async () => {
            if (!issue.liId) {
                return;
            }
            setIsLoading(true);
            const data = await get(`/v1.0/leases/master/issues/${issue.liId}/actions`);
            if (response.ok) {
                const nextActions = data || [];
                setActions(nextActions);
                onActionCountChange(nextActions.length);
            }
            setIsLoading(false);
        };
        fetchActions();
        // Actions are reloaded when the issue changes. Depending on use-http method
        // identities here would cause unnecessary reloads.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [issue.liId]);

    const handleAddAction = async () => {
        if (!issue.liId || !newAction.trim()) {
            return;
        }
        setIsSubmitting(true);
        await post(`/v1.0/leases/master/issues/${issue.liId}/actions`, { note: newAction });
        if (response.ok) {
            setNewAction('');
            const data = await get(`/v1.0/leases/master/issues/${issue.liId}/actions`);
            if (response.ok) {
                const nextActions = data || [];
                setActions(nextActions);
                onActionCountChange(nextActions.length);
            }
        }
        setIsSubmitting(false);
    };

    return (
        <div style={localStyles.inlineActionsPanel} onClick={(event) => event.stopPropagation()}>
            <div style={localStyles.inlineActionsHeader}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, color: styles.colors.gray900 }}>
                    <BiMessageDetail size={18} /> Actions
                </div>
                <span style={localStyles.actionCountPill}>
                    {actions.length} action{actions.length === 1 ? '' : 's'}
                </span>
            </div>

            <div style={localStyles.actionsList}>
                {isLoading ? (
                    <div style={localStyles.actionsEmpty}>Loading actions...</div>
                ) : actions.length === 0 ? (
                    <div style={localStyles.actionsEmpty}>No actions recorded yet.</div>
                ) : (
                    actions.map((action, idx) => (
                        <div key={`${action.lidId}-${idx}`} style={localStyles.actionItem}>
                            <div style={localStyles.actionNote}>{action.tranNote}</div>
                            <div style={localStyles.actionMeta}>
                                <span>
                                    By: {action.tranBy || 'Unknown'} {action.tranByNo ? `(${action.tranByNo})` : ''}
                                </span>
                                <span>{action.tranDate ? new Date(action.tranDate).toLocaleString() : '-'}</span>
                            </div>
                        </div>
                    ))
                )}
            </div>

            <div style={localStyles.inlineActionComposer}>
                <textarea
                    value={newAction}
                    onChange={(e) => setNewAction(e.target.value)}
                    placeholder="Add action note..."
                    rows={2}
                    style={{ ...localStyles.textarea, marginBottom: '0.5rem' }}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button onClick={handleAddAction} disabled={isSubmitting || !newAction.trim()} style={styles.buttonPrimary}>
                        {isSubmitting ? 'Adding...' : 'Add Action'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- Local Styles ---
const localStyles: { [key: string]: React.CSSProperties } = {
    emailButton: {
        border: `1px solid ${styles.colors.primary}`,
        borderRadius: '0.375rem',
        backgroundColor: '#FFFFFF',
        color: styles.colors.primary,
        padding: '0.4rem 0.65rem',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.35rem',
        fontSize: '0.8rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
    },
    emailFeedback: {
        marginTop: '0.75rem',
        borderRadius: '0.375rem',
        padding: '0.55rem 0.75rem',
        fontSize: '0.85rem',
    },
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(2px)',
    },
    modalContent: {
        backgroundColor: '#fff',
        borderRadius: '8px',
        width: '500px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
        display: 'flex',
        flexDirection: 'column',
    },
    modalHeader: {
        padding: '1rem 1.25rem',
        borderBottom: `1px solid ${styles.colors.border}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    modalBody: { padding: '1.25rem' },
    modalFooter: {
        padding: '1rem 1.25rem',
        borderTop: `1px solid ${styles.colors.border}`,
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '0.75rem',
        backgroundColor: styles.colors.gray50,
        borderRadius: '0 0 8px 8px',
    },
    summaryChips: {
        display: 'flex',
        gap: '0.5rem',
        flexWrap: 'wrap',
        marginTop: '0.5rem',
    },
    summaryChip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.3rem',
        border: '1px solid',
        borderRadius: '999px',
        padding: '0.3rem 0.7rem',
        fontSize: '0.85rem',
        fontWeight: 600,
        lineHeight: 1.2,
    },
    leaseLandman: {
        marginTop: '0.55rem',
        color: styles.colors.gray700,
        fontSize: '1rem',
        fontWeight: 600,
    },
    label: { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: styles.colors.gray700, marginBottom: '0.3rem' },
    textInput: {
        width: '100%',
        padding: '0.4rem 0.6rem',
        borderRadius: '4px',
        border: `1px solid ${styles.colors.gray300}`,
        fontSize: '0.9rem',
    },
    selectInput: {
        width: '100%',
        padding: '0.4rem 0.6rem',
        borderRadius: '4px',
        border: `1px solid ${styles.colors.gray300}`,
        fontSize: '0.9rem',
        backgroundColor: '#fff',
    },
    textarea: {
        width: '100%',
        padding: '0.5rem',
        borderRadius: '4px',
        border: `1px solid ${styles.colors.gray300}`,
        fontSize: '0.9rem',
        fontFamily: 'inherit',
        resize: 'vertical',
    },
    grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' },
    iconButton: { background: 'none', border: 'none', cursor: 'pointer', color: styles.colors.gray500, padding: 0 },
    closeIssueButton: {
        backgroundColor: styles.colors.white,
        border: `1px solid ${styles.colors.gray300}`,
        color: styles.colors.gray700,
        borderRadius: '6px',
        padding: '0.5rem 0.85rem',
        minHeight: '36px',
        cursor: 'pointer',
        fontSize: '0.9rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
    },
    editIssueButton: {
        backgroundColor: styles.colors.white,
        border: `1px solid ${styles.colors.gray300}`,
        color: styles.colors.gray600,
        borderRadius: '6px',
        width: '36px',
        height: '36px',
        padding: 0,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    resolutionChoices: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.35rem',
    },
    resolvedGoodButton: {
        backgroundColor: styles.colors.success,
        border: 'none',
        color: styles.colors.white,
        borderRadius: '6px',
        padding: '0.5rem 0.75rem',
        minHeight: '36px',
        cursor: 'pointer',
        fontSize: '0.85rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
    },
    resolvedBadButton: {
        backgroundColor: styles.colors.warning,
        border: 'none',
        color: styles.colors.white,
        borderRadius: '6px',
        padding: '0.5rem 0.75rem',
        minHeight: '36px',
        cursor: 'pointer',
        fontSize: '0.85rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
    },
    resolutionCancelButton: {
        backgroundColor: styles.colors.white,
        border: `1px solid ${styles.colors.gray300}`,
        color: styles.colors.gray600,
        borderRadius: '6px',
        width: '36px',
        height: '36px',
        padding: 0,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    saveBtn: {
        backgroundColor: styles.colors.success,
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        padding: '0.25rem 0.75rem',
        cursor: 'pointer',
        fontSize: '0.85rem',
    },
    cancelBtn: {
        backgroundColor: 'transparent',
        color: styles.colors.gray600,
        border: `1px solid ${styles.colors.gray300}`,
        borderRadius: '4px',
        padding: '0.25rem 0.75rem',
        cursor: 'pointer',
        fontSize: '0.85rem',
    },
    actionBtn: {
        backgroundColor: styles.colors.gray100,
        border: `1px solid ${styles.colors.gray300}`,
        color: styles.colors.gray700,
        borderRadius: '4px',
        padding: '0.25rem 0.5rem',
        cursor: 'pointer',
        fontSize: '0.8rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.25rem',
    },
    actionCountPill: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.25rem',
        color: styles.colors.primary,
        backgroundColor: '#dbeafe',
        border: `1px solid ${styles.colors.primaryLight}`,
        borderRadius: '999px',
        padding: '0.15rem 0.5rem',
        fontSize: '0.78rem',
        fontWeight: 600,
    },
    inlineActionsPanel: {
        marginTop: '1rem',
        border: `1px solid ${styles.colors.border}`,
        borderRadius: '6px',
        backgroundColor: styles.colors.white,
        overflow: 'hidden',
    },
    inlineActionsHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        padding: '0.75rem 1rem',
        backgroundColor: styles.colors.gray50,
        borderBottom: `1px solid ${styles.colors.border}`,
    },
    actionsList: {
        display: 'grid',
        gap: '0.75rem',
        padding: '1rem',
        maxHeight: '260px',
        overflowY: 'auto',
        backgroundColor: styles.colors.bgSecondary,
    },
    actionsEmpty: {
        textAlign: 'center',
        color: styles.colors.gray500,
        padding: '1.25rem',
        backgroundColor: styles.colors.white,
        border: `1px dashed ${styles.colors.gray300}`,
        borderRadius: '6px',
    },
    actionItem: {
        padding: '0.75rem',
        borderRadius: '6px',
        border: `1px solid ${styles.colors.border}`,
        backgroundColor: styles.colors.white,
    },
    actionNote: {
        fontSize: '0.9rem',
        color: styles.colors.gray900,
        marginBottom: '0.5rem',
        whiteSpace: 'pre-wrap',
    },
    actionMeta: {
        fontSize: '0.75rem',
        color: styles.colors.gray500,
        display: 'flex',
        justifyContent: 'space-between',
        gap: '1rem',
        flexWrap: 'wrap',
    },
    inlineActionComposer: {
        padding: '1rem',
        borderTop: `1px solid ${styles.colors.border}`,
        backgroundColor: styles.colors.white,
    },
};

export default IssuesSection;



// WEBPACK FOOTER //
// ./src/components/leases/sections/Issues.tsx